import React, { useState, useEffect } from 'react';
import { getIncidents, getCatalogTasks } from '@/data/jsonImports';

export function useJsonDebugger() {
  const [jsonData, setJsonData] = useState<{
    incidentsRaw: any[] | null;
    catalogTasksRaw: any[] | null;
    incidentsParsed: any[] | null;
    catalogTasksParsed: any[] | null;
    error: string | null;
  }>({
    incidentsRaw: null,
    catalogTasksRaw: null,
    incidentsParsed: null,
    catalogTasksParsed: null,
    error: null
  });

  useEffect(() => {
    try {
      console.log("🔍 JSON Debugger - Reading JSON files");
      
      const incidentsJson = getIncidents();
      const catalogTasksJson = getCatalogTasks();
      
      console.log("📄 Type of incidentsJson:", typeof incidentsJson);
      console.log("📄 Type of catalogTasksJson:", typeof catalogTasksJson);
      
      // Check if we have arrays and they're not empty
      const hasIncidentsContent = Array.isArray(incidentsJson) && incidentsJson.length > 0;
      const hasCatalogTasksContent = Array.isArray(catalogTasksJson) && catalogTasksJson.length > 0;
      
      if (hasIncidentsContent) {
        console.log("📊 Incidents JSON items:", incidentsJson.length);
        console.log("📊 Sample incident:", incidentsJson[0]);
        
        // Process incidents to normalize field names
        const parsedIncidents = incidentsJson.map(internalNormalizeTicketFields);
        console.log("📊 Parsed incidents count:", parsedIncidents.length);
        if (parsedIncidents.length > 0) {
          console.log("📊 Sample parsed incident:", parsedIncidents[0]);
        }
      } else {
        console.warn("⚠️ incidentsJson is not a valid array or is empty");
      }
      
      if (hasCatalogTasksContent) {
        console.log("📊 Catalog Tasks JSON items:", catalogTasksJson.length);
        console.log("📊 Sample catalog task:", catalogTasksJson[0]);
        
        // Process catalog tasks to normalize field names
        const parsedCatalogTasks = catalogTasksJson.map(internalNormalizeTicketFields);
        console.log("📊 Parsed catalog tasks count:", parsedCatalogTasks.length);
        if (parsedCatalogTasks.length > 0) {
          console.log("📊 Sample parsed catalog task:", parsedCatalogTasks[0]);
        }
      } else {
        console.warn("⚠️ catalogTasksJson is not a valid array or is empty");
      }
      
      setJsonData({
        incidentsRaw: hasIncidentsContent ? incidentsJson : null,
        catalogTasksRaw: hasCatalogTasksContent ? catalogTasksJson : null,
        incidentsParsed: hasIncidentsContent ? incidentsJson.map(internalNormalizeTicketFields) : null,
        catalogTasksParsed: hasCatalogTasksContent ? catalogTasksJson.map(internalNormalizeTicketFields) : null,
        error: (!hasIncidentsContent && !hasCatalogTasksContent) ? "JSON files are empty or not valid arrays" : null
      });
    } catch (err) {
      console.error("❌ Error in JSON Debugger:", err);
      setJsonData({
        incidentsRaw: null,
        catalogTasksRaw: null,
        incidentsParsed: null,
        catalogTasksParsed: null,
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }, []);

  return jsonData;
}

// Internal function to normalize ticket fields
function internalNormalizeTicketFields(ticket: any): any {
  const normalized = {...ticket};
  
  // Add lowercase versions of all fields for compatibility
  Object.keys(normalized).forEach(key => {
    if (!normalized[key.toLowerCase()] && key.toLowerCase() !== key) {
      normalized[key.toLowerCase()] = normalized[key];
    }
  });
  
  // Add specific field mappings for our application
  if (!normalized.number && normalized.Number) {
    normalized.number = normalized.Number;
  }
  
  if (!normalized.short_description && normalized["Short description"]) {
    normalized.short_description = normalized["Short description"];
  }
  
  if (!normalized.opened && normalized.Opened) {
    normalized.opened = normalized.Opened;
  }
  
  if (!normalized.state && normalized.State) {
    normalized.state = normalized.State;
  }
  
  if (!normalized.priority && normalized.Priority) {
    normalized.priority = normalized.Priority;
  }
  
  if (!normalized.assigned_to && normalized["Assigned to"]) {
    normalized.assigned_to = normalized["Assigned to"];
  }
  
  // Extract API name from description if not already set
  if (!normalized.api_name) {
    const extractApiRegex = /\b([a-z0-9-]+[-]?api)\b/i;
    const extractSystemRegex = /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i;
    const eamRegex = /\bEAM\b/i;
    
    const description = normalized.short_description || normalized["Short description"] || "";
    
    const apiMatch = description.match(extractApiRegex);
    if (apiMatch) {
      normalized.api_name = apiMatch[0];
    } else {
      const systemMatch = description.match(extractSystemRegex);
      if (systemMatch) {
        normalized.api_name = systemMatch[0];
      } else if (description.match(eamRegex)) {
        normalized.api_name = 'EAM-system';
      } else if (description.toLowerCase().includes('api')) {
        normalized.api_name = 'generic-api';
      }
    }
  }
  
  return normalized;
}
